"use client";

import { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Flex,
  Grid,
  Text,
  useMediaQuery,
  Drawer,
  CloseButton,
  VStack,
  HStack,
  RadioGroup,
  Image,
  Spinner,
  Input,
} from "@chakra-ui/react";
import { CiEdit, CiHeart, CiSearch } from "react-icons/ci";
import {
  MdOutlineContentCopy,
  MdOutlineKeyboardArrowDown,
} from "react-icons/md";
import { PiWhatsappLogoLight } from "react-icons/pi";
import {
  FaHeart,
  FaShoppingCart,
  FaUser,
  FaClipboardList,
  FaChartLine,
  FaBox,
  FaTicketAlt,
  FaDatabase,
  FaCog,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { toast } from "react-toastify";
import { AuthContext } from "../../contexts/AuthContext";

const normalizeImageUrl = (path) => {
  if (!path) return "";

  const normalized = path.replace(/\\/g, "/");

  const index = normalized.indexOf("/uploads");
  if (index === -1) return "";

  return `https://api.aqvex.com.br${normalized.substring(index)}`;
};

export default function Panel() {
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const [isMinorThan480] = useMediaQuery("(max-width: 480px)");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [sallers, setSallers] = useState([]);
  const [sallerCode, setSallerCode] = useState(null);
  const [clientHash, setClientHash] = useState("");
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const navigate = useNavigate();

  const handleCopy = async () => {
    const textoParaCopiar = `https://loja.aqvex.com.br/catalogo/${clientHash}/${sallerCode}`;

    await navigator.clipboard.writeText(textoParaCopiar);

    toast.success("Conteúdo copiado para a área de transferência");
  };

  const { user } = useContext(AuthContext);

  // Favoritos
  const [favorites, setFavorites] = useState([]);
  const [clearingFavorites, setClearingFavorites] = useState(false);

  const toggleFavorite = async (id) => {
    try {
      const response = await api.post(`/admin/products/${id}/favorite`);

      const { action } = response.data;

      if (action === "added") {
        setFavorites((prev) => [...prev, id]);
      } else {
        setFavorites((prev) => prev.filter((f) => f !== id));
      }
    } catch (error) {
      toast.error("Erro ao favoritar produto");
    }
  };

  const clearAllFavorites = async () => {
    setClearingFavorites(true);
    try {
      await Promise.all(
        favorites.map((id) => api.post(`/admin/products/${id}/favorite`)),
      );
      setFavorites([]);
    } catch {
      toast.error("Erro ao desfavoritar produtos");
    } finally {
      setClearingFavorites(false);
    }
  };

  useEffect(() => {
    const getFavorites = async () => {
      try {
        const res = await api.get("/admin/products-favorites");
        setFavorites(res.data.data);
      } catch {}
    };

    const getProducts = async () => {
      try {
        setLoading(true);
        const response = await api.get("/admin/products");
        setProducts(response.data.data);
      } catch {
        toast.error(
          "Não foi possível buscar os seus produtos, entre em contato com o suporte.",
        );
      } finally {
        setLoading(false);
      }
    };

    const getAllSallers = async () => {
      try {
        const response = await api.get("/admin/sallers");
        setSallers(response.data.data);
        setClientHash(response.data.clientHash);
      } catch {
        toast.error(
          "Não foi possível buscar os vendedores, entre em contato com o suporte.",
        );
      }
    };

    getAllSallers();
    getFavorites();
    getProducts();
  }, []);

  // Drawer
  const [open, setOpen] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState("");
  const [drawerMode, setDrawerMode] = useState("filter"); // "filter", "favorites", "tools"

  // Filtros
  const [ordenarValue, setOrdenarValue] = useState("padrao");
  const [promocaoValue, setPromocaoValue] = useState("sim");
  const [tamanhoValue, setTamanhoValue] = useState(null);
  const [categoriaValue, setCategoriaValue] = useState(null);

  const filteredProducts = products.filter(
    (product) =>
      product.prod_descricao.toLowerCase().includes(search.toLowerCase()) ||
      product.prod_referencia?.toLowerCase().includes(search.toLowerCase()),
  );

  const handleOpenDrawer = (title) => {
    setDrawerMode("filter");
    setDrawerTitle(title);
    setOpen(true);
  };

  const handleOpenFavorites = () => {
    setDrawerMode("favorites");
    setDrawerTitle("Favoritos");
    setOpen(true);
  };

  const handleOpenTools = () => {
    setDrawerMode("tools");
    setDrawerTitle("Ferramentas");
    setOpen(true);
  };

  const handleOpenLink = () => {
    setDrawerMode("link");
    setDrawerTitle("Gerar Link");
    setOpen(true);
  };

  const filterButtons = ["Ordenar"];
  const favoriteProducts = products.filter((p) =>
    favorites.includes(p.prod_id),
  );

  const sortedProducts = useMemo(() => {
    let list = [...filteredProducts];

    switch (ordenarValue) {
      case "novidades":
        list.sort((a, b) => b.prod_id - a.prod_id);
        break;

      case "menor":
        list.sort((a, b) => a.preco - b.preco);
        break;

      case "maior":
        list.sort((a, b) => b.preco - a.preco);
        break;

      case "padrao":
      default:
        break;
    }

    // Ordenar favoritos para o topo
    return list.sort((a, b) => {
      const aIsFavorite = favorites.includes(a.prod_id);
      const bIsFavorite = favorites.includes(b.prod_id);

      if (aIsFavorite && !bIsFavorite) return -1;
      if (!aIsFavorite && bIsFavorite) return 1;
      return 0;
    });
  }, [filteredProducts, ordenarValue, favorites]);

  const tools = [
    {
      name: "Pedidos",
      icon: (
        <Flex
          w="48px"
          h="48px"
          borderRadius="50%"
          bg="#E2E8F0"
          align="center"
          justify="center"
        >
          <FaClipboardList size={22} color="#4A5568" />
        </Flex>
      ),
      path: "/painel/relatorios-pedidos",
    },
    {
      name: "Clientes",
      icon: (
        <Flex
          w="48px"
          h="48px"
          borderRadius="50%"
          bg="#E2E8F0"
          align="center"
          justify="center"
        >
          <FaUser size={22} color="#4A5568" />
        </Flex>
      ),
      path: "/painel/clientes-cadastrados",
    },
    {
      name: "Carrinhos",
      icon: (
        <Flex
          w="48px"
          h="48px"
          borderRadius="50%"
          bg="#E2E8F0"
          align="center"
          justify="center"
        >
          <FaShoppingCart size={22} color="#4A5568" />
        </Flex>
      ),
      path: "/painel/carrinhos-abandonados",
    },
    {
      name: "Ranking",
      icon: (
        <Flex
          w="48px"
          h="48px"
          borderRadius="50%"
          bg="#E2E8F0"
          align="center"
          justify="center"
        >
          <FaChartLine size={22} color="#4A5568" />
        </Flex>
      ),
      path: "/painel/ranking",
    },
    {
      name: "Produtos",
      icon: (
        <Flex
          w="48px"
          h="48px"
          borderRadius="50%"
          bg="#E2E8F0"
          align="center"
          justify="center"
        >
          <FaBox size={22} color="#4A5568" />
        </Flex>
      ),
      path: "/painel/produtos",
    },
    {
      name: "Configurações",
      icon: (
        <Flex
          w="48px"
          h="48px"
          borderRadius="50%"
          bg="#E2E8F0"
          align="center"
          justify="center"
        >
          <FaCog size={22} color="#4A5568" />
        </Flex>
      ),
      path: "/painel/configuracoes-acesso",
    },
    ...(user?.info?.type === "master"
      ? [
          {
            name: "Database Config",
            icon: (
              <Flex
                w="48px"
                h="48px"
                borderRadius="50%"
                bg="#E2E8F0"
                align="center"
                justify="center"
              >
                <FaDatabase size={22} color="#4A5568" />
              </Flex>
            ),
            path: "/painel/database-config",
          },
        ]
      : []),
  ];

  return (
    <Flex
      flexDirection="column"
      width="100%"
      alignItems="center"
      minH="100vh"
      gap="20px"
    >
      {/* Header */}
      <Flex
        flexDirection="column"
        width="100%"
        alignItems="center"
        padding="15px"
        boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
        borderBottom="1px solid #e0e0e0"
      >
        <Flex
          minW={isLargerThan768 ? "1200px" : "100%"}
          justifyContent="space-between"
          alignItems="center"
        >
          <Image
            src="/logo-aqvex.png"
            onClick={() => navigate("/")}
            cursor="pointer"
          />
          {showSearch && (
            <Input
              maxW="300px"
              placeholder="Pesquisar..."
              padding="15px"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          <Flex gap="10px">
            <CiHeart size={30} cursor="pointer" onClick={handleOpenFavorites} />
            <CiSearch
              size={30}
              cursor="pointer"
              onClick={() => setShowSearch((prev) => !prev)}
            />
            <CiEdit size={30} cursor="pointer" onClick={handleOpenTools} />
            <PiWhatsappLogoLight
              size={30}
              cursor="pointer"
              onClick={handleOpenLink}
            />
          </Flex>
        </Flex>
      </Flex>
      <Flex
        width="100%"
        maxW={isLargerThan768 ? "1200px" : "100%"}
        padding="15px"
        justifyContent="center"
      >
        <Flex
          gap="10px"
          flexDirection="row"
          overflowX={isLargerThan768 ? "hidden" : "auto"}
          overflowY="hidden"
          width="100%"
        >
          {filterButtons.map((btn) => (
            <Button
              key={btn}
              flex={isLargerThan768 ? 1 : "none"}
              minH="40px"
              variant="outline"
              fontSize={isMinorThan480 ? ".7rem" : "1rem"}
              onClick={() => handleOpenDrawer(btn)}
              rightIcon={<MdOutlineKeyboardArrowDown />}
              flexShrink={isLargerThan768 ? 1 : 0}
              px="16px"
              whiteSpace="nowrap"
            >
              {btn}
            </Button>
          ))}
        </Flex>
      </Flex>
      <Flex maxW={isLargerThan768 ? "1200px" : "100%"} padding="15px">
        {loading && <Spinner />}
        <Grid
          templateColumns={isLargerThan768 ? "1fr 1fr 1fr 1fr" : "1fr 1fr"}
          gap={isLargerThan768 ? "20px" : "10px"}
          width="100%"
        >
          {sortedProducts.map((product) => (
            <Flex
              key={product.prod_id}
              flexDirection="column"
              onClick={() =>
                navigate(`/painel/produtos/${product.prod_id}/imagens`)
              }
              cursor="pointer"
            >
              <Image
                src={normalizeImageUrl(product.url_imagem)}
                minH="300px"
                maxH="300px"
              />
              <Flex padding="10px" justifyContent="space-between">
                <Flex flexDirection="column">
                  <Text fontWeight="600">{product.prod_descricao}</Text>
                  <Text fontSize="14px">#{product.prod_referencia}</Text>
                  <Text>R$ {product.preco.toFixed(2)}</Text>
                </Flex>
                {favorites.includes(product.prod_id) ? (
                  <FaHeart
                    size={30}
                    color="red"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(product.prod_id);
                    }}
                  />
                ) : (
                  <CiHeart
                    size={30}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(product.prod_id);
                    }}
                  />
                )}
              </Flex>
            </Flex>
          ))}
        </Grid>
      </Flex>

      {/* Drawer */}
      <Drawer.Root open={open} size="full">
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.CloseTrigger asChild>
              <CloseButton
                position="absolute"
                top="2"
                right="4"
                size="lg"
                onClick={() => setOpen(false)}
              />
            </Drawer.CloseTrigger>

            <Drawer.Header
              borderBottom="1px solid #e0e0e0"
              padding="15px"
              textAlign="center"
            >
              <Drawer.Title>{drawerTitle}</Drawer.Title>
            </Drawer.Header>

            <Drawer.Body padding="15px">
              {drawerMode === "tools" ? (
                <Grid
                  templateColumns={isLargerThan768 ? "1fr 1fr 1fr" : "1fr 1fr"}
                  gap="20px"
                >
                  {tools.map((tool) => (
                    <Flex
                      key={tool.name}
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      padding="20px"
                      border="1px solid #e0e0e0"
                      borderRadius="8px"
                      cursor="pointer"
                      minH="200px"
                      gap="20px"
                      onClick={() => navigate(tool.path)}
                    >
                      {tool.icon}
                      <Text fontWeight="600">{tool.name}</Text>
                    </Flex>
                  ))}
                </Grid>
              ) : drawerMode === "filter" ? (
                <>
                  {/* Mantém todo seu código atual de filtros */}
                  {drawerTitle === "Ordenar" && (
                    <RadioGroup.Root
                      value={ordenarValue}
                      onValueChange={(e) => setOrdenarValue(e.value)}
                    >
                      <VStack align="start" spacing="3">
                        {[
                          { label: "Padrão", value: "padrao" },
                          { label: "Últimas novidades", value: "novidades" },
                          { label: "Menor preço", value: "menor" },
                          { label: "Maior preço", value: "maior" },
                        ].map((item) => (
                          <RadioGroup.Item key={item.value} value={item.value}>
                            <RadioGroup.ItemHiddenInput />
                            <RadioGroup.ItemIndicator />
                            <RadioGroup.ItemText>
                              {item.label}
                            </RadioGroup.ItemText>
                          </RadioGroup.Item>
                        ))}
                      </VStack>
                    </RadioGroup.Root>
                  )}

                  {drawerTitle === "Promoção" && (
                    <RadioGroup.Root
                      value={promocaoValue}
                      onValueChange={setPromocaoValue}
                    >
                      <VStack align="start" spacing="3">
                        {[
                          { label: "Sim", value: "sim" },
                          { label: "Não", value: "nao" },
                        ].map((item) => (
                          <RadioGroup.Item key={item.value} value={item.value}>
                            <RadioGroup.ItemHiddenInput />
                            <RadioGroup.ItemIndicator />
                            <RadioGroup.ItemText>
                              {item.label}
                            </RadioGroup.ItemText>
                          </RadioGroup.Item>
                        ))}
                      </VStack>
                    </RadioGroup.Root>
                  )}

                  {drawerTitle === "Tamanhos" && (
                    <HStack spacing="3">
                      {["P", "M", "G", "GG"].map((val) => (
                        <Button
                          key={val}
                          onClick={() => setTamanhoValue(val)}
                          variant={tamanhoValue === val ? "solid" : "outline"}
                        >
                          {val}
                        </Button>
                      ))}
                    </HStack>
                  )}

                  {drawerTitle === "Categorias" && (
                    <HStack spacing="3">
                      {["Baby Look", "Capri", "Regata"].map((cat) => (
                        <Button
                          key={cat}
                          onClick={() => setCategoriaValue(cat)}
                          variant={categoriaValue === cat ? "solid" : "outline"}
                          padding="15px"
                        >
                          {cat}
                        </Button>
                      ))}
                    </HStack>
                  )}
                </>
              ) : drawerMode === "link" ? (
                <Flex width="100%" flexDirection="column" gap="20px">
                  <Text fontSize="14px">
                    Selecione o vendedor para gerar o link dinâmico do catálogo
                  </Text>
                  <Flex
                    flexDirection="column"
                    maxH="400px"
                    maxW="400px"
                    overflow="auto"
                    gap="10px"
                  >
                    {sallers.map((saller) => (
                      <Flex
                        key={saller.codigo}
                        padding="10px"
                        border={
                          sallerCode === saller.codigo
                            ? "1px solid #6aff07ff"
                            : "1px solid #ccc"
                        }
                        borderRadius="4px"
                        cursor="pointer"
                        justifyContent="space-between"
                        alignItems="center"
                        onClick={() => setSallerCode(saller.codigo)}
                      >
                        <Text>{saller.vendedor}</Text>
                        <Text>#{saller.codigo}</Text>
                      </Flex>
                    ))}
                  </Flex>
                  {sallerCode && (
                    <Flex
                      padding="10px"
                      border="1px solid #ccc"
                      borderRadius="4px"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Text>
                        {`https://loja.aqvex.com.br/catalogo/${clientHash}/${sallerCode}`}
                      </Text>
                      <MdOutlineContentCopy
                        cursor="pointer"
                        onClick={handleCopy}
                      />
                    </Flex>
                  )}
                </Flex>
              ) : (
                <Flex flexDirection="column" gap="20px">
                  {favoriteProducts.length > 0 && (
                    <Flex justifyContent="flex-end">
                      <Button
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        loading={clearingFavorites}
                        onClick={clearAllFavorites}
                        padding="15px"
                      >
                        Desfavoritar todos
                      </Button>
                    </Flex>
                  )}
                  <Grid
                    templateColumns={
                      isLargerThan768 ? "1fr 1fr 1fr 1fr" : "1fr 1fr"
                    }
                    gap="20px"
                  >
                    {favoriteProducts.length > 0 ? (
                      favoriteProducts.map((product) => (
                        <Flex key={product.prod_id} flexDirection="column">
                          <Image
                            src={normalizeImageUrl(product.url_imagem)}
                            minH="300px"
                            maxH="300px"
                            borderRadius="8px"
                            objectFit="cover"
                          />
                          <Flex
                            padding={isLargerThan768 ? "10px 0px" : "10px"}
                            alignItems="flex-start"
                            justifyContent="space-between"
                            gap="10px"
                          >
                            <Flex flexDirection="column" flex={1}>
                              <Text fontWeight="600">
                                {product.prod_descricao}
                              </Text>
                              <Text fontSize="14px">
                                #{product.prod_referencia}
                              </Text>
                              <Text>R$ {product.preco.toFixed(2)}</Text>
                            </Flex>
                            <FaHeart
                              size={30}
                              cursor="pointer"
                              color="red"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(product.prod_id);
                              }}
                            />
                          </Flex>
                        </Flex>
                      ))
                    ) : (
                      <Text>Nenhum produto favoritado.</Text>
                    )}
                  </Grid>
                </Flex>
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>
    </Flex>
  );
}
